import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  PlusIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon,
  MagnifyingGlassIcon, ChevronDownIcon, DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { useAuthStore } from '../../stores/authStore';
import { api, mealsAPI } from '../../services/api';

type TrainerClientOption = { id: string; firstName: string; lastName: string };

const planSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  description: z.string().max(500).optional(),
  tags: z.array(z.string()).optional(),
});
type PlanFormData = z.infer<typeof planSchema>;

interface FoodLibraryItem {
  _id: string;
  name: string;
  servingSize?: number;
  servingMeasure?: string;
  calories?: number;
  protein?: number;
  totalCarbs?: number;
  fat?: number;
  foodType?: string;
}

interface MealFoodItem {
  foodId?: string;
  foodName: string;
  servingSize: number;
  servingMeasure: string;
  foodType: 'Carb' | 'Protein' | 'Fat' | 'Supplement' | 'Ingredient';
  calories?: number;
  protein?: number;
  carbs?: number;
  fat?: number;
  recipe?: string;
}

interface MealEntry {
  mealNumber: number;
  mealLabel?: string;
  notes?: string;
  foodItems: MealFoodItem[];
}

function scaleLibraryFood(food: FoodLibraryItem, servings: number): MealFoodItem {
  const n = Number.isFinite(servings) && servings > 0 ? servings : 1;
  const scale = (value?: number) =>
    value == null || Number.isNaN(value) ? undefined : Math.round(value * n * 100) / 100;

  return {
    foodId: food._id,
    foodName: food.name,
    servingSize: Math.round((food.servingSize || 1) * n * 100) / 100,
    servingMeasure: food.servingMeasure || 'serving',
    foodType: (food.foodType as MealFoodItem['foodType']) || 'Ingredient',
    calories: scale(food.calories),
    protein: scale(food.protein),
    carbs: scale(food.totalCarbs),
    fat: scale(food.fat),
  };
}

/** Strip client completion fields and renumber for create/update/copy. */
function sanitizeMealsForSave(meals: MealEntry[]): MealEntry[] {
  return meals.map((m, i) => ({
    mealNumber: i + 1,
    mealLabel: m.mealLabel || undefined,
    notes: m.notes || undefined,
    foodItems: m.foodItems.map((f) => ({
      foodId: f.foodId,
      foodName: f.foodName,
      servingSize: f.servingSize,
      servingMeasure: f.servingMeasure,
      foodType: f.foodType,
      calories: f.calories,
      protein: f.protein,
      carbs: f.carbs,
      fat: f.fat,
      recipe: f.recipe || undefined,
    })),
  }));
}

let foodCache: FoodLibraryItem[] | null = null;

export default function MealPlanBuilder() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId');
  const mealPlanId = searchParams.get('mealPlanId');
  const { user } = useAuthStore();

  const { control, handleSubmit, formState: { errors }, reset, getValues } = useForm<PlanFormData>({
    resolver: zodResolver(planSchema),
    defaultValues: { name: '', startDate: '', endDate: '', description: '', tags: [] },
  });

  const [meals, setMeals] = useState<MealEntry[]>([{ mealNumber: 1, mealLabel: '', notes: '', foodItems: [] }]);
  const [expandedMeals, setExpandedMeals] = useState<Set<number>>(new Set([0]));
  const [foods, setFoods] = useState<FoodLibraryItem[]>([]);
  const [filteredFoods, setFilteredFoods] = useState<FoodLibraryItem[]>([]);
  const [foodSearch, setFoodSearch] = useState('');
  const [showFoodSelector, setShowFoodSelector] = useState(false);
  const [targetMealIndex, setTargetMealIndex] = useState<number | null>(null);
  const [pendingFood, setPendingFood] = useState<FoodLibraryItem | null>(null);
  const [numberOfServings, setNumberOfServings] = useState(1);
  const [addFoodDraft, setAddFoodDraft] = useState<MealFoodItem | null>(null);
  const [isLoadingFoods, setIsLoadingFoods] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingPlan, setIsLoadingPlan] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [clients, setClients] = useState<TrainerClientOption[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copyPlanName, setCopyPlanName] = useState('');
  const [copySelectedClientId, setCopySelectedClientId] = useState('');
  const [copyStartDate, setCopyStartDate] = useState('');
  const [copyEndDate, setCopyEndDate] = useState('');
  const [isCopyingPlan, setIsCopyingPlan] = useState(false);

  // Load foods
  const fetchFoods = useCallback(async () => {
    if (foodCache) { setFoods(foodCache); setFilteredFoods(foodCache); return; }
    try {
      setIsLoadingFoods(true);
      const res = await api.get('/libraries/foods');
      const list = res.data.foods || res.data || [];
      foodCache = list;
      setFoods(list);
      setFilteredFoods(list);
    } catch (e) {
      console.error('Error loading foods:', e);
    } finally {
      setIsLoadingFoods(false);
    }
  }, []);

  useEffect(() => { fetchFoods(); }, [fetchFoods]);

  // Fetch trainer's clients (for copy-to-client)
  useEffect(() => {
    const loadClients = async () => {
      try {
        setIsLoadingClients(true);
        const response = await api.get('/clients');
        setClients(response.data.clients || response.data || []);
      } catch (e) {
        console.error('Error fetching clients:', e);
      } finally {
        setIsLoadingClients(false);
      }
    };
    loadClients();
  }, []);

  useEffect(() => {
    if (!foodSearch.trim()) { setFilteredFoods(foods); return; }
    const q = foodSearch.toLowerCase();
    setFilteredFoods(foods.filter(f => f.name.toLowerCase().includes(q)));
  }, [foodSearch, foods]);

  // Load existing plan for editing
  useEffect(() => {
    if (!mealPlanId) return;
    const load = async () => {
      try {
        setIsLoadingPlan(true);
        const res = await mealsAPI.getById(mealPlanId);
        const plan = res.data;
        reset({
          name: plan.name,
          startDate: plan.startDate ? plan.startDate.split('T')[0] : '',
          endDate: plan.endDate ? plan.endDate.split('T')[0] : '',
          description: plan.description || '',
          tags: plan.tags || [],
        });
        setMeals(plan.meals || []);
        setExpandedMeals(new Set());
        setIsEditing(true);
      } catch (e) {
        console.error('Error loading meal plan:', e);
      } finally {
        setIsLoadingPlan(false);
      }
    };
    load();
  }, [mealPlanId, reset]);

  useEffect(() => {
    if (!user?.roles?.includes('TRAINER')) navigate('/dashboard');
    if (!clientId) navigate('/trainer/clients');
  }, [user, clientId, navigate]);

  // Meal management
  const addMeal = () => {
    setMeals(prev => [...prev, { mealNumber: prev.length + 1, mealLabel: '', notes: '', foodItems: [] }]);
    setExpandedMeals(new Set([meals.length]));
  };

  const removeMeal = (idx: number) => {
    if (meals.length <= 1) return;
    setMeals(prev => prev.filter((_, i) => i !== idx).map((m, i) => ({ ...m, mealNumber: i + 1 })));
  };

  const duplicateMeal = (idx: number) => {
    setMeals((prev) => {
      const source = prev[idx];
      const clone: MealEntry = {
        mealNumber: prev.length + 1,
        mealLabel: source.mealLabel ? `${source.mealLabel} (copy)` : '',
        notes: source.notes || '',
        foodItems: source.foodItems.map((f) => ({ ...f })),
      };
      const next = [...prev];
      next.splice(idx + 1, 0, clone);
      return next.map((m, i) => ({ ...m, mealNumber: i + 1 }));
    });
    setExpandedMeals((prev) => new Set([...Array.from(prev), idx + 1]));
  };

  const moveMeal = (idx: number, dir: 'up' | 'down') => {
    const next = [...meals];
    const target = dir === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setMeals(next.map((m, i) => ({ ...m, mealNumber: i + 1 })));
  };

  const updateMealField = (idx: number, field: 'mealLabel' | 'notes', value: string) => {
    setMeals(prev => prev.map((m, i) => i === idx ? { ...m, [field]: value } : m));
  };

  const toggleMeal = (idx: number) => {
    const next = new Set(expandedMeals);
    next.has(idx) ? next.delete(idx) : next.add(idx);
    setExpandedMeals(next);
  };

  // Food management — single Add Food form (CF-style: food + servings + macros together)
  const emptyAddDraft = (): MealFoodItem => ({
    foodName: '',
    servingSize: 1,
    servingMeasure: 'serving',
    foodType: 'Ingredient',
  });

  const openFoodSelector = (mealIdx: number) => {
    setTargetMealIndex(mealIdx);
    setFoodSearch('');
    setPendingFood(null);
    setNumberOfServings(1);
    setAddFoodDraft(emptyAddDraft());
    setShowFoodSelector(true);
  };

  const closeFoodSelector = () => {
    setShowFoodSelector(false);
    setTargetMealIndex(null);
    setPendingFood(null);
    setNumberOfServings(1);
    setAddFoodDraft(null);
  };

  const applyLibraryFoodToForm = (food: FoodLibraryItem, servings: number) => {
    setPendingFood(food);
    setNumberOfServings(servings);
    setAddFoodDraft(scaleLibraryFood(food, servings));
  };

  const handleServingsChange = (raw: string) => {
    const next = parseFloat(raw);
    const servings = Number.isFinite(next) && next > 0 ? next : 0;
    setNumberOfServings(servings || 0);
    if (pendingFood && servings > 0) {
      setAddFoodDraft((prev) => ({
        ...scaleLibraryFood(pendingFood, servings),
        recipe: prev?.recipe,
      }));
    }
  };

  const updateAddFoodDraft = (field: keyof MealFoodItem, value: any) => {
    setAddFoodDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const confirmAddFoodToMeal = () => {
    if (targetMealIndex === null || !addFoodDraft?.foodName.trim()) return;
    const item: MealFoodItem = {
      ...addFoodDraft,
      foodName: addFoodDraft.foodName.trim(),
      servingSize: addFoodDraft.servingSize || 0,
      servingMeasure: addFoodDraft.servingMeasure || 'serving',
    };
    setMeals((prev) =>
      prev.map((m, i) =>
        i === targetMealIndex ? { ...m, foodItems: [...m.foodItems, item] } : m
      )
    );
    closeFoodSelector();
  };

  const removeFoodFromMeal = (mealIdx: number, foodIdx: number) => {
    setMeals(prev => prev.map((m, i) =>
      i === mealIdx ? { ...m, foodItems: m.foodItems.filter((_, fi) => fi !== foodIdx) } : m
    ));
  };

  const duplicateFoodInMeal = (mealIdx: number, foodIdx: number) => {
    setMeals((prev) =>
      prev.map((m, i) => {
        if (i !== mealIdx) return m;
        const clone = { ...m.foodItems[foodIdx] };
        const foodItems = [...m.foodItems];
        foodItems.splice(foodIdx + 1, 0, clone);
        return { ...m, foodItems };
      })
    );
  };

  const updateFoodField = (mealIdx: number, foodIdx: number, field: keyof MealFoodItem, value: any) => {
    setMeals(prev => prev.map((m, i) =>
      i === mealIdx
        ? { ...m, foodItems: m.foodItems.map((f, fi) => fi === foodIdx ? { ...f, [field]: value } : f) }
        : m
    ));
  };

  const hasAnyFoods = meals.some((m) => m.foodItems.length > 0);

  const openCopyModal = () => {
    const current = getValues();
    const currentName = current.name || '';
    setCopyPlanName(currentName ? `Copy of ${currentName}` : 'Copy of Meal Plan');
    setCopySelectedClientId(clientId || '');
    setCopyStartDate(current.startDate || '');
    setCopyEndDate(current.endDate || '');
    setShowCopyModal(true);
  };

  const handleCopyPlan = async () => {
    if (!copyPlanName.trim() || !copySelectedClientId || !copyStartDate || !copyEndDate) {
      setError('Please fill in all required fields for copying');
      return;
    }
    if (copyEndDate < copyStartDate) {
      setError('Copy end date must be on or after start date');
      return;
    }
    if (!hasAnyFoods) {
      setError('Add at least one food before copying this meal plan');
      return;
    }

    setIsCopyingPlan(true);
    setError(null);
    try {
      const current = getValues();
      const response = await mealsAPI.create({
        name: copyPlanName.trim(),
        startDate: copyStartDate,
        endDate: copyEndDate,
        description: current.description || undefined,
        tags: current.tags,
        assignedTo: copySelectedClientId,
        meals: sanitizeMealsForSave(meals),
      });
      const newId = response.data?.id || response.data?._id;
      setShowCopyModal(false);
      if (!newId) {
        setError('Meal plan copied but no ID was returned');
        return;
      }
      navigate(
        `/trainer/meal-builder?clientId=${copySelectedClientId}&mealPlanId=${newId}`
      );
    } catch (e: any) {
      console.error('Error copying meal plan:', e);
      setError(e.response?.data?.error || e.message || 'Failed to copy meal plan');
    } finally {
      setIsCopyingPlan(false);
    }
  };

  const onSubmit = async (data: PlanFormData) => {
    if (!clientId) return;
    if (!hasAnyFoods) {
      setError('Add at least one food to a meal before saving');
      return;
    }
    if (data.endDate < data.startDate) {
      setError('End date must be on or after start date');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const payload = {
        ...data,
        assignedTo: clientId,
        meals: sanitizeMealsForSave(meals),
      };
      if (isEditing && mealPlanId) {
        await mealsAPI.update(mealPlanId, payload);
        setShowSuccessModal(true);
      } else {
        const response = await mealsAPI.create(payload);
        const newId = response.data?.id || response.data?._id;
        if (!newId) {
          setError('Meal plan created but no ID was returned');
          return;
        }
        navigate(`/trainer/meal-builder?clientId=${clientId}&mealPlanId=${newId}`);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to save meal plan');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!user?.roles?.includes('TRAINER') || !clientId) return null;
  if (isLoadingPlan) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const FOOD_TYPES: MealFoodItem['foodType'][] = ['Carb', 'Protein', 'Fat', 'Supplement', 'Ingredient'];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <h1 className="text-lg font-medium text-gray-900">
          {isEditing ? 'Edit Meal Plan' : 'Create Meal Plan'}
        </h1>
        <div className="flex items-center space-x-2">
          {isEditing && mealPlanId && (
            <button
              type="button"
              onClick={openCopyModal}
              className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
            >
              <DocumentDuplicateIcon className="h-4 w-4" />
              <span>Copy</span>
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate(clientId ? `/trainer/clients/${clientId}` : '/trainer/clients')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Update Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Meal Plan Updated</h3>
                <button
                  type="button"
                  onClick={() => setShowSuccessModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-700">Your changes have been saved.</p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowSuccessModal(false)}
                className="w-full px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy Meal Plan Modal */}
      {showCopyModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white w-full max-w-md mx-4 rounded-lg shadow-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Copy Meal Plan</h3>
                <button
                  type="button"
                  onClick={() => setShowCopyModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
                <input
                  type="text"
                  value={copyPlanName}
                  onChange={(e) => setCopyPlanName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assign to Client *</label>
                <select
                  value={copySelectedClientId}
                  onChange={(e) => setCopySelectedClientId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  disabled={isLoadingClients}
                >
                  <option value="">Select a client...</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>
                      {client.firstName} {client.lastName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    value={copyStartDate}
                    onChange={(e) => setCopyStartDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                  <input
                    type="date"
                    value={copyEndDate}
                    onChange={(e) => setCopyEndDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex space-x-3">
              <button
                type="button"
                onClick={() => setShowCopyModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCopyPlan}
                disabled={
                  isCopyingPlan ||
                  !copyPlanName.trim() ||
                  !copySelectedClientId ||
                  !copyStartDate ||
                  !copyEndDate
                }
                className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isCopyingPlan ? 'Copying...' : 'Copy Meal Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Food Modal — one form like CF: food, servings, size/macros editable */}
      {showFoodSelector && addFoodDraft && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-end sm:items-center justify-center">
          <div className="bg-white w-full max-w-lg max-h-[90vh] flex flex-col rounded-t-lg sm:rounded-lg">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-4 rounded-t-lg z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Add Food</h3>
                <button
                  type="button"
                  onClick={closeFoodSelector}
                  className="text-gray-500 hover:text-gray-900"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              {/* Food picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Food *</label>
                {addFoodDraft.foodName ? (
                  <div className="flex items-center justify-between rounded-lg border border-gray-300 px-3 py-2 bg-gray-50">
                    <span className="text-sm font-medium text-gray-900">{addFoodDraft.foodName}</span>
                    <button
                      type="button"
                      onClick={() => {
                        setPendingFood(null);
                        setNumberOfServings(1);
                        setAddFoodDraft(emptyAddDraft());
                        setFoodSearch('');
                      }}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="relative mb-2">
                      <MagnifyingGlassIcon className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={foodSearch}
                        onChange={(e) => setFoodSearch(e.target.value)}
                        placeholder="Search foods..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                      {isLoadingFoods ? (
                        <div className="text-center py-4 text-gray-500 text-sm">Loading foods...</div>
                      ) : filteredFoods.length === 0 ? (
                        <div className="text-center py-4 text-gray-500 text-sm">
                          {foodSearch ? 'No foods match your search' : 'No foods in library'}
                        </div>
                      ) : (
                        filteredFoods.map((food) => (
                          <button
                            key={food._id}
                            type="button"
                            onClick={() => applyLibraryFoodToForm(food, 1)}
                            className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900 text-sm">{food.name}</div>
                            {(food.calories != null || food.servingSize) && (
                              <div className="text-xs text-gray-500">
                                {food.servingSize && `${food.servingSize} ${food.servingMeasure}`}
                                {food.calories != null && ` · ${food.calories} cal`}
                              </div>
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Servings + size/measure/calories — same form as CF */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Servings</label>
                  <input
                    type="number"
                    min="0.1"
                    step="0.1"
                    value={numberOfServings || ''}
                    onChange={(e) => handleServingsChange(e.target.value)}
                    disabled={!pendingFood}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Serving Size</label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={addFoodDraft.servingSize}
                    onChange={(e) =>
                      updateAddFoodDraft('servingSize', parseFloat(e.target.value) || 0)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Serv. Measure</label>
                  <input
                    type="text"
                    value={addFoodDraft.servingMeasure}
                    onChange={(e) => updateAddFoodDraft('servingMeasure', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Calories</label>
                  <input
                    type="number"
                    min="0"
                    value={addFoodDraft.calories ?? ''}
                    onChange={(e) =>
                      updateAddFoodDraft(
                        'calories',
                        e.target.value === '' ? undefined : parseFloat(e.target.value)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fat</label>
                  <input
                    type="number"
                    min="0"
                    value={addFoodDraft.fat ?? ''}
                    onChange={(e) =>
                      updateAddFoodDraft(
                        'fat',
                        e.target.value === '' ? undefined : parseFloat(e.target.value)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Carb</label>
                  <input
                    type="number"
                    min="0"
                    value={addFoodDraft.carbs ?? ''}
                    onChange={(e) =>
                      updateAddFoodDraft(
                        'carbs',
                        e.target.value === '' ? undefined : parseFloat(e.target.value)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Prot</label>
                  <input
                    type="number"
                    min="0"
                    value={addFoodDraft.protein ?? ''}
                    onChange={(e) =>
                      updateAddFoodDraft(
                        'protein',
                        e.target.value === '' ? undefined : parseFloat(e.target.value)
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Food Type</label>
                <select
                  value={addFoodDraft.foodType}
                  onChange={(e) =>
                    updateAddFoodDraft('foodType', e.target.value as MealFoodItem['foodType'])
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {FOOD_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Recipe</label>
                <textarea
                  value={addFoodDraft.recipe || ''}
                  onChange={(e) => updateAddFoodDraft('recipe', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="border-t border-gray-200 px-4 py-3 flex space-x-3">
              <button
                type="button"
                onClick={closeFoodSelector}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmAddFoodToMeal}
                disabled={!addFoodDraft.foodName.trim()}
                className="flex-1 px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="py-4 space-y-4">

        {/* Plan Details */}
        <div className="bg-white rounded-lg shadow-sm mx-0">
          <div className="flex items-center justify-between p-3 border-b border-gray-100">
            <h3 className="text-base font-semibold text-gray-900">Plan Details</h3>
          </div>
          <div className="p-3 space-y-3">
            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
              <Controller name="name" control={control} render={({ field }) => (
                <input {...field} type="text" placeholder="e.g. Week 1 Nutrition Plan"
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-gray-300'}`} />
              )} />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
            </div>
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                <Controller name="startDate" control={control} render={({ field }) => (
                  <input {...field} type="date"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.startDate ? 'border-red-500' : 'border-gray-300'}`} />
                )} />
                {errors.startDate && <p className="text-red-500 text-xs mt-1">{errors.startDate.message}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date *</label>
                <Controller name="endDate" control={control} render={({ field }) => (
                  <input {...field} type="date"
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent ${errors.endDate ? 'border-red-500' : 'border-gray-300'}`} />
                )} />
                {errors.endDate && <p className="text-red-500 text-xs mt-1">{errors.endDate.message}</p>}
              </div>
            </div>
            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <Controller name="description" control={control} render={({ field }) => (
                <textarea {...field} rows={2} placeholder="Optional notes for the client"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent" />
              )} />
            </div>
          </div>
        </div>

        {/* Meals */}
        <div className="space-y-3">
          {meals.map((meal, mealIdx) => {
            const isOpen = expandedMeals.has(mealIdx);
            const mealCals = meal.foodItems.reduce((s, f) => s + (f.calories || 0), 0);
            return (
              <div key={mealIdx} className="bg-white rounded-lg shadow-sm">
                {/* Meal Header */}
                <div className="flex items-center p-3 border-b border-gray-100">
                  <div
                    className="flex items-center space-x-2 flex-1 cursor-pointer"
                    onClick={() => toggleMeal(mealIdx)}
                  >
                    <ChevronDownIcon className={`h-4 w-4 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                    <div>
                      <span className="text-sm font-semibold text-gray-900">
                        Meal {meal.mealNumber}{meal.mealLabel ? ` — ${meal.mealLabel}` : ''}
                      </span>
                      <span className="ml-2 text-xs text-gray-500">
                        {meal.foodItems.length} item{meal.foodItems.length !== 1 ? 's' : ''}
                        {mealCals > 0 && ` · ${Math.round(mealCals)} cal`}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      type="button"
                      onClick={() => duplicateMeal(mealIdx)}
                      title="Duplicate meal"
                      className="p-1 text-gray-400 hover:text-green-600"
                    >
                      <DocumentDuplicateIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => moveMeal(mealIdx, 'up')} disabled={mealIdx === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                      <ArrowUpIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => moveMeal(mealIdx, 'down')} disabled={mealIdx === meals.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30">
                      <ArrowDownIcon className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={() => removeMeal(mealIdx)} disabled={meals.length <= 1}
                      className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Meal Body */}
                {isOpen && (
                  <div className="p-3 space-y-3">
                    {/* Label & Notes */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Label (optional)</label>
                        <input
                          type="text"
                          value={meal.mealLabel || ''}
                          onChange={e => updateMealField(mealIdx, 'mealLabel', e.target.value)}
                          placeholder="e.g. Breakfast"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
                        <input
                          type="text"
                          value={meal.notes || ''}
                          onChange={e => updateMealField(mealIdx, 'notes', e.target.value)}
                          placeholder="e.g. Eat within 30 min of waking"
                          className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                        />
                      </div>
                    </div>

                    {/* Food Items */}
                    {meal.foodItems.length > 0 && (
                      <div className="space-y-2">
                        {meal.foodItems.map((food, foodIdx) => (
                          <div key={foodIdx} className="border border-gray-200 rounded-lg p-2 space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-gray-900">{food.foodName}</span>
                              <div className="flex items-center space-x-1">
                                <button
                                  type="button"
                                  onClick={() => duplicateFoodInMeal(mealIdx, foodIdx)}
                                  title="Duplicate food"
                                  className="text-gray-400 hover:text-green-600"
                                >
                                  <DocumentDuplicateIcon className="h-4 w-4" />
                                </button>
                                <button type="button" onClick={() => removeFoodFromMeal(mealIdx, foodIdx)}
                                  className="text-red-400 hover:text-red-600">
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="block text-xs text-gray-500 mb-0.5">Serving Size</label>
                                <input type="number" min="0" step="0.1"
                                  value={food.servingSize}
                                  onChange={e => updateFoodField(mealIdx, foodIdx, 'servingSize', parseFloat(e.target.value) || 0)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-0.5">Unit</label>
                                <input type="text"
                                  value={food.servingMeasure}
                                  onChange={e => updateFoodField(mealIdx, foodIdx, 'servingMeasure', e.target.value)}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-500 mb-0.5">Type</label>
                                <select
                                  value={food.foodType}
                                  onChange={e => updateFoodField(mealIdx, foodIdx, 'foodType', e.target.value as MealFoodItem['foodType'])}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                >
                                  {FOOD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                                </select>
                              </div>
                            </div>
                            <div className="grid grid-cols-4 gap-1">
                              {(['calories', 'protein', 'carbs', 'fat'] as const).map(macro => (
                                <div key={macro}>
                                  <label className="block text-xs text-gray-500 mb-0.5 capitalize">{macro === 'calories' ? 'Cal' : macro.charAt(0).toUpperCase() + macro.slice(1)}</label>
                                  <input type="number" min="0"
                                    value={(food as any)[macro] ?? ''}
                                    onChange={e => updateFoodField(mealIdx, foodIdx, macro, parseFloat(e.target.value) || undefined)}
                                    className="w-full px-1.5 py-1 border border-gray-300 rounded text-sm" />
                                </div>
                              ))}
                            </div>
                            <div>
                              <label className="block text-xs text-gray-500 mb-0.5">Recipe / Prep Notes</label>
                              <input type="text"
                                value={food.recipe || ''}
                                onChange={e => updateFoodField(mealIdx, foodIdx, 'recipe', e.target.value)}
                                placeholder="Optional preparation instructions"
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add Food Button */}
                    <button
                      type="button"
                      onClick={() => openFoodSelector(mealIdx)}
                      className="w-full flex items-center justify-center space-x-2 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-500 hover:text-green-600 transition-colors text-sm"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Add Food</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}

          {/* Add Meal Button */}
          <button
            type="button"
            onClick={addMeal}
            className="w-full flex items-center justify-center space-x-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-green-500 hover:text-green-600 transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Add Meal {meals.length + 1}</span>
          </button>
        </div>

        {/* Error & Submit */}
        <div className="px-4 pb-8 space-y-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}
          <button
            type="submit"
            disabled={isSubmitting || !hasAnyFoods}
            className="w-full py-3 px-4 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Meal Plan' : 'Create Meal Plan'}
          </button>
          {!hasAnyFoods && (
            <p className="text-xs text-center text-gray-500">Add at least one food before saving.</p>
          )}
        </div>
      </form>
    </div>
  );
}
